import type {
  PatinfoProfileDto,
  PatinfoSearchInclude,
  PatinfoSearchResultDto,
  PatinfoSearchTermDto,
} from '../../types/api/patinfo';
import type { ProviderProfileService } from '../ProviderProfileService';
import {
  EMPTY_CLIENT_SEARCH_VALUES,
  type ClientSearchValues,
} from './ClientCacheContracts';

export const DEFAULT_CLIENT_SEARCH_BLOCK_SIZE = '10';

/**
 * Client Info always manages launches ("launch") and Reports always manages
 * report history ("history"), so both are included on every client search
 * regardless of which account features are enabled.
 */
const DEFAULT_CLIENT_SEARCH_INCLUDES: PatinfoSearchInclude[] = ['launch', 'history'];

/**
 * Maps account "Enables" setting names to the PatinfoSearchInclude value that
 * unlocks the corresponding ProfileRowEditor section's data. "enable_flagmgmt"
 * and "enable_patrefmgmt" are both accepted for "flags" since the backend has
 * used either name for the same setting.
 */
const ENABLE_FLAG_TO_SEARCH_INCLUDE: Record<string, PatinfoSearchInclude> = {
  enable_schedulemgmt: 'schedule',
  enable_patconnmgmt: 'connections',
  enable_flagmgmt: 'flags',
  enable_patrefmgmt: 'flags',
  enable_medinfomgmt: 'extended',
  enable_episodemgmt: 'episodes',
  enable_messagedirect: 'messages',
};

/**
 * Resolves the PatinfoSearchRequestDto.searchIncludes list for a client search
 * from the account's raw Enables map (lowercased setting name -> boolean).
 */
export function resolveClientSearchIncludes(enablesMap: Record<string, boolean>): PatinfoSearchInclude[] {
  const includes = new Set<PatinfoSearchInclude>(DEFAULT_CLIENT_SEARCH_INCLUDES);

  Object.entries(ENABLE_FLAG_TO_SEARCH_INCLUDE).forEach(([flagName, include]) => {
    if (enablesMap[flagName]) {
      includes.add(include);
    }
  });

  return Array.from(includes);
}

/**
 * Every run*Search function below infers `hasMore` purely from "did this page come back full"
 * (rowCount >= pageSize) since the backend response carries no total-row-count field — so it can
 * be wrong whenever the true row count is an exact multiple of the page size, leaving Next
 * enabled one page past the real end. Fetching that nonexistent next page then comes back with
 * 0 rows. Rather than have the backend compute and return a total count on every search just to
 * cover this edge case, ProGroupsClientsView.tsx's onNextPage handlers use this to detect that
 * overshoot and fall back to the last good page (keeping its data and Prev/Next controls)
 * instead of committing the empty page.
 */
export function isOvershotEmptyPage(requestedBlocknum: string, rowCount: number): boolean {
  return requestedBlocknum !== '0' && rowCount === 0;
}

export interface ClientSearchResult {
  profiles: PatinfoProfileDto[];
  blocknum: string;
  blocksize: string;
  hasMore: boolean;
  lastTerms: PatinfoSearchTermDto[];
  searchValues: ClientSearchValues;
}

export function termsToClientSearchValues(terms: PatinfoSearchTermDto[]): ClientSearchValues {
  const next: ClientSearchValues = { ...EMPTY_CLIENT_SEARCH_VALUES };
  terms.forEach((term) => {
    if (term.searchField in next) {
      (next as any)[term.searchField] = term.searchTerm;
    }
  });
  return next;
}

export async function runClientProfileSearch(
  profileService: ProviderProfileService,
  terms: PatinfoSearchTermDto[],
  blocknum: string,
  searchValues?: ClientSearchValues,
  blocksize: string = DEFAULT_CLIENT_SEARCH_BLOCK_SIZE,
  searchIncludes: PatinfoSearchInclude[] = DEFAULT_CLIENT_SEARCH_INCLUDES
): Promise<ClientSearchResult> {
  const resolvedValues = searchValues ?? termsToClientSearchValues(terms);
  const response = await profileService.searchProfiles({
    type: 'profiles',
    blocknum,
    blocksize,
    searchIncludes,
    scope: 'practice',
    searchTerms: terms,
    searchFilters: [],
  });

  const profiles = response.profiles ?? [];
  const pageSize = parseInt(blocksize, 10);

  return {
    profiles,
    blocknum: response.blocknum ?? blocknum,
    blocksize: response.blocksize ?? blocksize,
    hasMore: profiles.length >= pageSize,
    lastTerms: terms,
    searchValues: resolvedValues,
  };
}

export const DEFAULT_RECENT_REPORTS_BLOCK_SIZE = '10';

export interface RecentReportsSearchResult {
  results: PatinfoSearchResultDto[];
  profiles: PatinfoProfileDto[];
  blocknum: string;
  blocksize: string;
  hasMore: boolean;
}

/**
 * Recent Reports: an account-wide, reverse-chronological feed of every completed
 * administration — unlike runClientProfileSearch, there are no user search terms/filters
 * (the backend always returns everything, paginated). `searchIncludes` still matters (same
 * account-settings-driven list as Manage Clients) since it controls which sections of each
 * returned PatinfoProfileDto are populated (history is what this feed actually needs).
 */
export async function runRecentReportsSearch(
  profileService: ProviderProfileService,
  blocknum: string,
  blocksize: string = DEFAULT_RECENT_REPORTS_BLOCK_SIZE,
  searchIncludes: PatinfoSearchInclude[] = DEFAULT_CLIENT_SEARCH_INCLUDES
): Promise<RecentReportsSearchResult> {
  const response = await profileService.searchProfiles({
    type: 'recentreports',
    blocknum,
    blocksize,
    searchIncludes,
    scope: 'practice',
    searchTerms: [],
    searchFilters: [],
  });

  const results = response.results ?? [];
  const profiles = response.profiles ?? [];
  const pageSize = parseInt(blocksize, 10);

  return {
    results,
    profiles,
    blocknum: response.blocknum ?? blocknum,
    blocksize: response.blocksize ?? blocksize,
    hasMore: results.length >= pageSize,
  };
}

export const DEFAULT_OPEN_ADMINS_BLOCK_SIZE = '10';

export interface OpenAdminsSearchResult {
  profiles: PatinfoProfileDto[];
  blocknum: string;
  blocksize: string;
  hasMore: boolean;
}

/**
 * Open Admins: currently-launched profiles (those with a pending "Next" touchpoint date),
 * in chronological order — like Recent Reports, there are no user search terms/filters (the
 * backend always returns the full open-admin set, paginated); the row display is otherwise
 * identical to Manage Clients (full PatinfoProfileDto per row, ProfileRowEditor's own
 * Last Report/View Report showing each profile's most recent history entry).
 */
export async function runOpenAdminsSearch(
  profileService: ProviderProfileService,
  blocknum: string,
  blocksize: string = DEFAULT_OPEN_ADMINS_BLOCK_SIZE,
  searchIncludes: PatinfoSearchInclude[] = DEFAULT_CLIENT_SEARCH_INCLUDES
): Promise<OpenAdminsSearchResult> {
  const response = await profileService.searchProfiles({
    type: 'openadmins',
    blocknum,
    blocksize,
    searchIncludes,
    scope: 'practice',
    searchTerms: [],
    searchFilters: [],
  });

  const profiles = response.profiles ?? [];
  const pageSize = parseInt(blocksize, 10);

  return {
    profiles,
    blocknum: response.blocknum ?? blocknum,
    blocksize: response.blocksize ?? blocksize,
    hasMore: profiles.length >= pageSize,
  };
}

export const DEFAULT_MANAGE_FLAGS_BLOCK_SIZE = '10';

export interface ManageFlagsSearchResult {
  profiles: PatinfoProfileDto[];
  blocknum: string;
  blocksize: string;
  hasMore: boolean;
}

/**
 * Manage Flags: profiles with flags pending, in chronological order — like Recent
 * Reports/Open Admins, no user search terms/filters (the backend returns the full pending-flag
 * set, paginated). Row display currently mirrors Manage Clients (see Open Admins) — will be
 * combed out further later.
 */
export async function runManageFlagsSearch(
  profileService: ProviderProfileService,
  blocknum: string,
  blocksize: string = DEFAULT_MANAGE_FLAGS_BLOCK_SIZE,
  searchIncludes: PatinfoSearchInclude[] = DEFAULT_CLIENT_SEARCH_INCLUDES
): Promise<ManageFlagsSearchResult> {
  const response = await profileService.searchProfiles({
    type: 'flags',
    blocknum,
    blocksize,
    searchIncludes,
    scope: 'practice',
    searchTerms: [],
    searchFilters: [],
  });

  const profiles = response.profiles ?? [];
  const pageSize = parseInt(blocksize, 10);

  return {
    profiles,
    blocknum: response.blocknum ?? blocknum,
    blocksize: response.blocksize ?? blocksize,
    hasMore: profiles.length >= pageSize,
  };
}

export const DEFAULT_MANAGE_MESSAGES_BLOCK_SIZE = '10';

export interface ManageMessagesSearchResult {
  profiles: PatinfoProfileDto[];
  blocknum: string;
  blocksize: string;
  hasMore: boolean;
}

/**
 * Manage Messages: profiles with messages pending, in chronological order — like Recent
 * Reports/Open Admins/Manage Flags, no user search terms/filters (the backend returns the full
 * pending-message set, paginated). Row display currently mirrors Manage Clients (see Open
 * Admins) — will be combed out further later.
 */
export async function runManageMessagesSearch(
  profileService: ProviderProfileService,
  blocknum: string,
  blocksize: string = DEFAULT_MANAGE_MESSAGES_BLOCK_SIZE,
  searchIncludes: PatinfoSearchInclude[] = DEFAULT_CLIENT_SEARCH_INCLUDES
): Promise<ManageMessagesSearchResult> {
  const response = await profileService.searchProfiles({
    type: 'messages',
    blocknum,
    blocksize,
    searchIncludes,
    scope: 'practice',
    searchTerms: [],
    searchFilters: [],
  });

  const profiles = response.profiles ?? [];
  const pageSize = parseInt(blocksize, 10);

  return {
    profiles,
    blocknum: response.blocknum ?? blocknum,
    blocksize: response.blocksize ?? blocksize,
    hasMore: profiles.length >= pageSize,
  };
}
